/* „Nowy program", variant D — `docs/design/plan-propozycje-2026-09-21/index.html#d/create/*`.
 *
 * The WINDOW is `shared/ui/splitDialog.module.css` and the segmented control is
 * `shared/ui/segmented.module.css`; nothing either of them states is restated here. A declaration
 * written on both sides is settled by chunk order, which differs between the dev server and the
 * build.
 *
 * Every colour, space, radius, shadow, duration and type value is a token. Widths, heights and grid
 * tracks are layout and are deliberately outside the gate. */

.question {
  margin: var(--space-0);
  font-size: var(--fs-dialog-title);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  line-height: var(--lh-display);
}

/* ── A field ────────────────────────────────────────────────────────────────────────────────── */

.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-6px);
  min-width: var(--space-0);
  margin: var(--space-0);
  padding: var(--space-0);
  border: 0;
}

.label {
  display: flex;
  gap: var(--space-7px);
  align-items: baseline;
  padding: var(--space-0);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.required {
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.spacer {
  flex: 1;
}

.counter {
  font-size: var(--fs-caption);
  font-variant-numeric: tabular-nums;
  color: var(--text-3);
}

.help {
  font-size: var(--fs-micro);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

.input,
.textarea {
  box-sizing: border-box;
  width: 100%;
  padding: var(--space-0) var(--space-3);
  font: inherit;
  font-size: var(--fs-dialog-field);
  color: var(--text);
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-sm);
}

.input {
  height: var(--h-ex-wizard-field);
}

.textarea {
  height: var(--h-create-textarea);
  padding: var(--space-10px) var(--space-3);
  resize: none;
}

.input::placeholder,
.textarea::placeholder {
  color: var(--text-3);
}

.input:focus-visible,
.textarea:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

/* ── The category tiles ─────────────────────────────────────────────────────────────────────── */

.categories {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-2);
}

/* The border's ink is passed in as a custom property rather than written on the shorthand: the
 * value gate reads real property names only, and it refuses `transparent` on a border because it
 * cannot tell a deliberately invisible edge from a forgotten colour. */
.categoryTile {
  --tile-ink: transparent;

  position: relative;
  display: flex;
  gap: var(--space-9px);
  align-items: center;
  padding: var(--space-10px) var(--space-11px);
  text-align: left;
  background: var(--bg-inset);
  border: var(--border-thick) solid var(--tile-ink);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-tile-resting);
  cursor: pointer;
}

/* Chosen is an INK border and an inset fill, never the category's own colour as a wash: the tint
 * is what the owner banned on 16 September, and the dot already carries the hue. */
.categoryTileOn {
  --tile-ink: var(--text);

  background: var(--bg-card);
  box-shadow: none;
}

.categoryDot {
  flex: none;
  width: var(--space-2);
  height: var(--space-2);
  background: var(--cat-ink);
  border-radius: var(--r-pill);
}

.categoryName {
  font-size: var(--fs-note);
  font-weight: var(--fw-bold);
  color: var(--text);
}

/* ── The right half ─────────────────────────────────────────────────────────────────────────── */

.systemHead {
  display: flex;
  gap: var(--space-3);
  align-items: center;
}

.systemTitle {
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
}

.systemNote {
  font-size: var(--fs-note);
  color: var(--text-2);
}

/* A copy's system, stated rather than offered: a read-only row with a lock. Neutral — nothing on it
 * can be done, so nothing on it takes the accent. */
.systemLocked {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.lockedRow {
  display: flex;
  gap: var(--space-3);
  align-items: flex-start;
  padding: var(--space-3) var(--space-14px);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-12px);
}

.lockGlyph {
  flex: none;
  width: var(--space-4);
  height: var(--space-4);
  margin-top: var(--space-2px);
  color: var(--text-2);
}

.lockedText {
  display: flex;
  flex-direction: column;
  gap: var(--space-3px);
}

.lockedValue {
  font-size: var(--fs-button-sm);
  font-weight: var(--fw-extrabold);
  color: var(--text);
}

/* ── The steps, in the head ─────────────────────────────────────────────────────────────────── */

/* „Program · Szczegóły". Neutral in every state: the current step is lifted in ink, never in the
 * accent, which this window spends on the chosen start and the one primary. */
.steps {
  display: flex;
  gap: var(--space-6px);
  align-items: center;
  margin: var(--space-0);
  padding: var(--space-0);
  list-style: none;
}

.step {
  display: flex;
  gap: var(--space-2);
  align-items: center;
  height: var(--h-control);
  padding: var(--space-0) var(--space-3) var(--space-0) var(--space-6px);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-bold);
  color: var(--text-2);
  border-radius: var(--radius-8px);
}

.stepOn {
  color: var(--text);
  background: var(--bg-chip);
}

.step + .step::before {
  width: 18px;
  height: 1px;
  margin-right: var(--space-6px);
  content: '';
  background: var(--border-2);
}

.stepIndex {
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  font-size: var(--fs-caption);
  font-weight: var(--fw-extrabold);
  font-variant-numeric: tabular-nums;
  color: var(--text-2);
  background: var(--bg-chip);
  border-radius: var(--radius-5px);
}

.stepIndexOn {
  color: var(--on-ink);
  background: var(--text);
}

.stepIndex svg {
  width: var(--space-3);
  height: var(--space-3);
}

/* Both illustrations stand in this box at this height, so the segment's switch changes ink and
 * never geometry — which is what makes the two comparable at all. */
.systemViz {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-6);
  align-items: center;
  justify-content: center;
  padding: var(--space-7) var(--space-10) var(--space-3);
  text-align: center;
}

.systemDrawing {
  width: var(--w-viz-ambient);
}

.systemBody {
  max-width: 44ch;
  margin: var(--space-0);
  font-size: var(--fs-note);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.systemBody b {
  font-weight: var(--fw-bold);
  color: var(--text);
}

.lock {
  margin: var(--space-0);
  font-size: var(--fs-label);
  line-height: var(--lh-hint);
  color: var(--text-2);
}

/* The one sentence a copy owes the trainer: what happens to the template, and whose the copy is.
 * Neutral — it informs, and the pane's accent is spent on the choice. Both steps draw it. */
.note {
  margin: var(--space-0);
  padding: var(--space-3) var(--space-14px);
  font-size: var(--fs-note);
  line-height: var(--lh-hint);
  color: var(--text-2);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-12px);
}

/* ── The foot ───────────────────────────────────────────────────────────────────────────────── */

/* The chosen start beside „Dalej", one line, cut with an ellipsis. It shrinks against the spacer
 * before it and never against the primary, so a long template name cannot move the button. */
.startSummary {
  min-width: var(--space-0);
  overflow: hidden;
  font-size: var(--fs-note);
  line-height: var(--lh-hint);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The native radio does the work off screen, wearing `shared/ui/visuallyHiddenControl`. The tile
 * and the cover are the positioned ancestors it is placed against, and the rings are drawn on them
 * because those are the controls the trainer can see. */
.categoryTile:has(input:focus-visible) {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

/* The create window arriving from the leftover-plan prompt: the prompt held its scrim at full
 * strength through its own exit, so this one starts there rather than fading in from nothing —
 * which would dip the page for the length of the fade. The window itself still rises in. Doubled
 * class for the specificity: see `ResumePlanDialog.module.css`. */
.scrimArrives.scrimArrives[data-starting-style] {
  opacity: 1;
}

/* The same hand-over's window: it rises 8px as it fades in, so the prompt's exit and this entrance
 * read as one motion. Only here — a window opened in place fades without travelling (see
 * `shared/ui/splitDialog`). The transition property is widened for this arrival alone, with the
 * class doubled so it wins over the shared file's whatever order the two chunks load in. */
.risesIn.risesIn {
  transition-property: opacity, transform;
}

.risesIn.risesIn[data-starting-style] {
  transform: translate(-50%, calc(-50% + var(--space-2)));
}
